
import React from 'react';
import { Ball, SimulationConfig, MorphData, TrailCircle } from '@/types';
import { ShapeUtils } from '../physics/ShapeUtils';
import { DrawerLogic } from './DrawerLogic';
import { TrailLogic } from './TrailLogic';

export const BasicLogic = {
  updateBall: (b: Ball, effectiveCfg: SimulationConfig, now: number, hue: number, i: number, stepTS: number) => {
    if (effectiveCfg.ballRotationMode === 'SPIN') {
        b.angle = (b.angle || 0) + (b.rotationSpeed || 0.1) * stepTS;
    } else if (effectiveCfg.ballRotationMode === 'VELOCITY') {
        b.angle = Math.atan2(b.vel.y, b.vel.x);
    }

    if (effectiveCfg.ballWeightGrowthEnabled) {
        const spawnTime = b.spawnTime || now;
        const elapsed = (now - spawnTime) / 1000;
        const progress = Math.min(elapsed / (effectiveCfg.ballWeightGrowthTime || 60), 1.0);
        b.mass = (effectiveCfg.ballMinWeight || 1.0) + ((effectiveCfg.ballMaxWeight || 10.0) - (effectiveCfg.ballMinWeight || 1.0)) * progress;
    }

    if (effectiveCfg.ballGrowthMode === 'NONE') b.radius = effectiveCfg.ballRadius;

    DrawerLogic.updateBall(b, effectiveCfg, now, stepTS);

    if (effectiveCfg.enableTrails) {
        TrailLogic.updateBall(b, effectiveCfg, now, stepTS);
    }

    const useRGB = effectiveCfg.rgbLighting && effectiveCfg.useBallRGB;
    if (useRGB) b.color = `hsl(${hue}, 100%, 50%)`;
    else if (!effectiveCfg.ballPlacementIndividualEnabled) {
        // Universal color logic
        if (!b.isBox) {
            b.color = i === 0 ? effectiveCfg.ballColor : (i === 1 ? effectiveCfg.ball2Color : (effectiveCfg.ballColor || '#ffffff'));
        }
    }

    if (!b.isBox) {
        b.style = effectiveCfg.ballStyle;
        b.shape = effectiveCfg.ballShape || 'CIRCLE';
    }
  },

  handleGrowthAndShrink: (
    balls: Ball[],
    effectiveCfg: SimulationConfig,
    deltaTime: number,
    containerRadiusRef: any,
    ballGrowthRateRef: any,
    containerShrinkRateRef: any,
    morphData: MorphData,
    centerX: number,
    centerY: number,
    onBorderBreak: (b: Ball) => void
  ) => {
    if (effectiveCfg.ballGrowthMode === 'TIME') {
        const minContainerR = ShapeUtils.getMinRadius(morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadiusRef.current, effectiveCfg.containerRadius) - (effectiveCfg.containerLineWidth || 4) / 2;
        balls.forEach(b => {
            const remainsInside = !effectiveCfg.enableEscape && !effectiveCfg.enableSpaceSpiral;
            const maxAllowed = remainsInside ? Math.min(effectiveCfg.ballMaxRadius, minContainerR) : (effectiveCfg.ballMaxRadius || 1000);
            const minAllowed = effectiveCfg.ballMinRadius || 1;
            
            const growth = ballGrowthRateRef.current * (deltaTime / 1000);
            const nextRadius = b.radius + growth;
            
            if (effectiveCfg.enableTrails && effectiveCfg.trailEnableBorderBreak && b.radius < maxAllowed && nextRadius >= maxAllowed) onBorderBreak(b);
            
            const actualGrowth = Math.max(minAllowed, Math.min(maxAllowed, nextRadius)) - b.radius;
            b.radius += actualGrowth;
            
            if (remainsInside) {
                const relX = b.pos.x, relY = b.pos.y;
                const dist = Math.sqrt(relX * relX + relY * relY);
                if (dist + b.radius > minContainerR) {
                    const pushBack = (dist + b.radius) - minContainerR + 0.5;
                    if (dist > 0.1) {
                        b.pos.x -= (relX / dist) * pushBack; b.pos.y -= (relY / dist) * pushBack;
                    }
                }
            }
        });
        if (effectiveCfg.ballGrowthAcceleration !== 0) ballGrowthRateRef.current += effectiveCfg.ballGrowthAcceleration * (deltaTime / 1000);
    }
    if (effectiveCfg.containerShrinkMode === 'TIME') {
        const shrink = containerShrinkRateRef.current * (deltaTime / 1000) * 10; 
        containerRadiusRef.current = Math.max(effectiveCfg.minContainerRadius, containerRadiusRef.current - shrink);
        if (effectiveCfg.containerShrinkAcceleration !== 0) containerShrinkRateRef.current += effectiveCfg.containerShrinkAcceleration * (deltaTime / 1000);
    }
  },

  handleMorph: (
    deltaTime: number,
    effectiveCfg: SimulationConfig,
    morphTimerRef: React.MutableRefObject<number>,
    morphStateRef: React.MutableRefObject<{ active: boolean, from: string, to: string, progress: number }>,
    availableShapes: string[],
    setConfig: any
  ) => {
    if (effectiveCfg.shapeMorphEnabled) {
        const shapes = (effectiveCfg.morphableShapes && effectiveCfg.morphableShapes.length > 0) ? effectiveCfg.morphableShapes : availableShapes;
        if (effectiveCfg.shapeMorphContinuous) {
            morphStateRef.current.active = true;
            const cycleTime = Math.max(100, effectiveCfg.shapeMorphDuration || 1000);
            morphStateRef.current.progress += deltaTime / cycleTime;
            if (morphStateRef.current.progress >= 1) {
                morphStateRef.current.progress = 0;
                let currentIndex = shapes.indexOf(morphStateRef.current.to);
                if (currentIndex === -1) currentIndex = 0;
                const nextShape = shapes[(currentIndex + 1) % shapes.length];
                morphStateRef.current.from = morphStateRef.current.to;
                morphStateRef.current.to = nextShape;
                setConfig((prev: SimulationConfig) => ({ ...prev, containerShape: morphStateRef.current.from })); 
            }
        } else {
            morphTimerRef.current += deltaTime;
            const interval = (effectiveCfg.shapeMorphSpeed || 1.0) * 2000;
            if (morphTimerRef.current > interval && !morphStateRef.current.active) {
                morphTimerRef.current = 0;
                let currentIndex = shapes.indexOf(effectiveCfg.containerShape);
                if (currentIndex === -1) currentIndex = 0;
                const nextShape = shapes[(currentIndex + 1) % shapes.length];
                morphStateRef.current.active = true;
                morphStateRef.current.from = effectiveCfg.containerShape;
                morphStateRef.current.to = nextShape;
                morphStateRef.current.progress = 0;
            }
            if (morphStateRef.current.active) {
                morphStateRef.current.progress += deltaTime / (effectiveCfg.shapeMorphDuration || 1000);
                if (morphStateRef.current.progress >= 1) {
                    morphStateRef.current.progress = 1;
                    if (effectiveCfg.containerShape !== morphStateRef.current.to) {
                        setConfig((prev: SimulationConfig) => ({ ...prev, containerShape: morphStateRef.current.to }));
                    } else {
                        morphStateRef.current.active = false;
                    }
                }
            }
        }
    } else {
        morphStateRef.current.active = false;
        if (morphStateRef.current.from !== effectiveCfg.containerShape) {
             morphStateRef.current.from = effectiveCfg.containerShape;
             morphStateRef.current.to = effectiveCfg.containerShape;
             morphStateRef.current.progress = 0;
        }
    }
  },

  handleZoom: (effectiveCfg: SimulationConfig, escapeRingsCount: number, zoomLevelRef: any) => {
      if (effectiveCfg.enableEscape && effectiveCfg.escapeZoomEffectEnabled) {
          const total = Math.max(1, effectiveCfg.escapeRingCount);
          const progress = Math.max(0, Math.min(1, (total - escapeRingsCount) / total));
          const targetZoom = 1.0 - (1.0 - effectiveCfg.escapeZoomOutMax) * progress;
          const speed = effectiveCfg.escapeZoomOutSpeed || 0.05;
          zoomLevelRef.current += (targetZoom - zoomLevelRef.current) * speed;
      } else {
          if (Math.abs(zoomLevelRef.current - 1.0) > 0.001) {
               zoomLevelRef.current += (1.0 - zoomLevelRef.current) * 0.1;
          } else {
               zoomLevelRef.current = 1.0;
          }
      }
  },

  init: (
    effectiveCfg: SimulationConfig,
    createBall: (overrides: Partial<Ball>) => Ball,
    getInitialBallState: (index: number, total: number, config?: any) => { pos: { x: number, y: number }, vel: { x: number, y: number } },
    now: number = Date.now()
  ): Ball[] => {
    const total = Math.max(1, effectiveCfg.initialBalls || 1);
    return Array.from({ length: total }).map((_, i) => {
        const individualConfig = effectiveCfg.ballPlacementIndividualEnabled ? effectiveCfg.ballPlacementConfigs?.[i] : null;
        const { pos, vel } = getInitialBallState(i, total, individualConfig);
        return createBall({ id: `init-${i}`, pos, vel, radius: effectiveCfg.ballRadius, color: (individualConfig as any)?.color || (i === 0 ? effectiveCfg.ballColor : (i === 1 ? effectiveCfg.ball2Color : (effectiveCfg.ballColor || '#ffffff'))), spawnTime: now });
    });
  },

  renderTrail: (ctx: CanvasRenderingContext2D, trailCircles: TrailCircle[], config: SimulationConfig, webgl: any, camera: any, hue: number, balls: Ball[], zoom: number) => {
      if (!config.trailEnabled && trailCircles.length === 0) return;
      
      ctx.save();
      if (webgl) {
          webgl.renderTrails(trailCircles, config, camera, zoom);
      } else {
          const total = trailCircles.length;
          const centerX = ctx.canvas.width / 2;
          const centerY = ctx.canvas.height / 2;
          const dpr = window.devicePixelRatio || 1;
          const totalScale = (camera.scale || 1) * zoom * dpr;

          trailCircles.forEach((c, i) => {
              const pr = total > 1 ? i / (total - 1) : 1;
              const alphaFade = config.trailPersistence ? 1.0 : pr;
              
              const screenX = (c.pos.x + (camera.x || 0)) * totalScale + centerX;
              const screenY = (c.pos.y + (camera.y || 0)) * totalScale + centerY;
              
              const scale = config.trailMatchBallSize ? 1.0 : (config.trailEndScale + (config.trailStartScale - config.trailEndScale) * pr);
              const drawRadius = c.radius * scale * (config.trailWidthMultiplier || 1.0) * totalScale;

              if (config.trailGlowAmount && config.trailGlowAmount > 0) {
                  ctx.shadowColor = config.trailCustomColor || '#ffffff';
                  ctx.shadowBlur = config.trailGlowAmount * totalScale;
              } else {
                  ctx.shadowColor = 'transparent';
                  ctx.shadowBlur = 0;
              }

              ctx.globalAlpha = alphaFade * (config.trailOpacity || 1.0);
              ctx.beginPath();
              ctx.fillStyle = (config.useTrailRGB !== false)
                  ? `hsl(${(c.hue !== undefined ? c.hue : hue + i * 2) % 360}, 100%, 50%)`
                  : config.trailCustomColor || '#ffffff';
              ctx.arc(screenX, screenY, drawRadius, 0, Math.PI * 2);
              ctx.fill();

              // Reset shadow for stroke
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;

              const showBorder = !config.trailPersistence || config.trailApplyBorderToPersistence;
              if (showBorder && config.trailStrokeWidth && config.trailStrokeWidth > 0) {
                  ctx.beginPath();
                  ctx.arc(screenX, screenY, drawRadius, 0, Math.PI * 2);
                  ctx.strokeStyle = config.trailStrokeColor || '#000000';
                  ctx.lineWidth = config.trailStrokeWidth * totalScale;
                  ctx.globalAlpha = alphaFade * (config.trailStrokeOpacity !== undefined ? config.trailStrokeOpacity : 1.0);
                  ctx.stroke();
              }
          });
      }
      ctx.restore();
  },

  enforceGlobalBoundaries: (
      balls: Ball[],
      effectiveCfg: SimulationConfig,
      rotRad: number,
      morphData: MorphData,
      containerRadius: number
  ) => {
      if (!effectiveCfg.enableSpaceSpiral && !effectiveCfg.enableEscape) {
          balls.forEach(b => {
              const relX = b.pos.x, relY = b.pos.y;
              const dist = Math.sqrt(relX * relX + relY * relY);
              const angle = Math.atan2(relY, relX);
              const lineWidth = effectiveCfg.containerLineWidth || 4;
              const effectiveShapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadius, morphData?.baseRadius) - lineWidth / 2;
              
              if (dist + b.radius > effectiveShapeR) {
                  const overlap = (dist + b.radius) - effectiveShapeR;
                  const localPos = { x: relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad), y: relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad) };
                  const localNormal = ShapeUtils.getNormal(localPos, morphData?.from || effectiveCfg.containerShape, containerRadius - lineWidth / 2, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, morphData?.baseRadius);
                  const worldNormal = { x: localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad), y: localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad) };
                  b.pos.x -= worldNormal.x * (overlap + 0.5); b.pos.y -= worldNormal.y * (overlap + 0.5);
              }
          });
      }
  }
};
